<template>
    <component
        :is="as"
        :class="[
            'base-card',
            themeClasses[theme],
            isInteractive && 'is-interactive',
            selected && 'is-selected',
            paddingClasses[padding],
            radiusClasses[radius],
            elevated && 'shadow-md',
        ]">
        <slot />
    </component>
</template>

<script lang="ts">
export type BaseCardTheme = "default" | "muted" | "accent" | "assessment" | "cream" | "cream-muted" | "bare";
export type BaseCardPadding = "none" | "sm" | "md" | "lg";
export type BaseCardRadius = "md" | "lgr" | "lg" | "xl";
export type BaseCardAs = "div" | "a" | "button" | "article" | "section";

interface BaseCardProps {
    theme?: BaseCardTheme;
    padding?: BaseCardPadding;
    radius?: BaseCardRadius;
    // Forces interactive styling on non-link/button tags. `as="a"` and
    // `as="button"` are interactive by default, so the prop is rarely needed.
    interactive?: boolean;
    // Soft lift (shadow-md) for cards that float over the page, e.g. in-chat tool cards.
    elevated?: boolean;
    // Selected state for a card that toggles, e.g. a pickable person with `as="button"` and
    // `aria-pressed`: accent border on the selected option fill.
    selected?: boolean;
    as?: BaseCardAs;
}
</script>

<script setup lang="ts">
import { computed } from "vue";

const { theme = "default", padding = "md", radius = "lg", interactive = false, elevated = false, selected = false, as = "div" } = defineProps<BaseCardProps>();

const themeClasses: Record<BaseCardTheme, string> = {
    default: "theme-default",
    muted: "theme-muted",
    accent: "theme-accent",
    assessment: "theme-assessment",
    cream: "theme-cream",
    "cream-muted": "theme-cream-muted",
    // No surface of its own: the click target inside a card an ancestor already paints, such as
    // a stretched button whose ::before overlay makes the whole surrounding card clickable.
    bare: "theme-bare",
};

const paddingClasses: Record<BaseCardPadding, string> = {
    none: "",
    sm: "p-4",
    md: "p-5",
    lg: "p-6",
};

// `lg` is the default corner and the one a theme can restyle via --radius-card.
const radiusClasses: Record<BaseCardRadius, string> = {
    md: "rounded-lg",
    lgr: "rounded-lgr",
    lg: "rounded-card",
    xl: "rounded-2xl",
};

// `as="a"` and `as="button"` carry interactive semantics; the prop only needs
// to be set to opt a plain container (div/article/section) into hover styling.
const isInteractive = computed(() => interactive || as === "a" || as === "button");
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .base-card {
        @apply block;
    }

    .base-card.theme-default {
        @apply border border-card-border bg-surface-raised;
    }

    .base-card.theme-muted {
        @apply border border-transparent bg-surface-sunken;
    }

    .base-card.theme-accent {
        @apply border border-card-accent-border bg-card-accent-bg;
    }

    .base-card.theme-assessment {
        @apply border border-card-assessment-border bg-card-assessment-bg;
    }

    .base-card.theme-cream {
        @apply border border-card-cream-border bg-surface-raised;
    }

    .base-card.theme-cream-muted {
        @apply border border-transparent bg-card-cream-muted-bg;
    }

    .base-card.theme-bare {
        @apply border-0 bg-transparent;
    }

    .base-card.is-interactive {
        @apply cursor-pointer transition-[colors,shadow] hover:shadow-sm;
    }

    /* The painted card around a bare one gives the hover feedback. */
    .base-card.theme-bare.is-interactive {
        @apply hover:shadow-none;
    }

    .base-card.is-selected {
        @apply border-control-accent bg-toggle-option-selected;
    }
}
</style>
